import { Drawer, Dropdown, Form, Input, InputNumber, Select, Switch, Tag } from "antd";
import { debounce, get } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const CONDITION_TYPES = [
  { value: "email_opened", label: "Email ouvert" },
  { value: "email_clicked", label: "Email cliqué" },
  { value: "email_replied", label: "Email répondu" },
  { value: "linkedin_accepted", label: "LinkedIn accepté" },
  { value: "has_email", label: "A un email" },
  { value: "has_linkedin", label: "A un LinkedIn" },
  { value: "has_phone", label: "A un téléphone" },
  { value: "lead_score_above", label: "Score lead supérieur à" },
  { value: "custom_variable", label: "Variable personnalisée" },
];

const TASK_TYPES = [
  { value: "call", label: "Appel" },
  { value: "note", label: "Note" },
  { value: "review", label: "Revue" },
  { value: "custom", label: "Personnalisé" },
];

const PLACEHOLDERS = [
  { key: "{{prénom}}", label: "Prénom" },
  { key: "{{nom}}", label: "Nom" },
  { key: "{{email}}", label: "Email" },
  { key: "{{entreprise}}", label: "Entreprise" },
  { key: "{{poste}}", label: "Poste" },
  { key: "{{téléphone}}", label: "Téléphone" },
  { key: "{{nom_complet}}", label: "Nom complet" },
  { key: "{{expéditeur}}", label: "Nom expéditeur" },
];

const PlaceholderDropdown = ({ onInsert }) => {
  const items = PLACEHOLDERS.map((p) => ({
    key: p.key,
    label: (
      <span style={{ fontSize: 13 }}>
        <Tag color="blue" style={{ marginRight: 6, fontSize: 11 }}>{p.key}</Tag>
        {p.label}
      </span>
    ),
  }));

  return (
    <Dropdown
      menu={{ items, onClick: ({ key }) => onInsert(key) }}
      trigger={["click"]}
    >
      <button
        type="button"
        className="crm-placeholder-btn"
        title="Insérer une variable"
      >
        {"{x}"}
      </button>
    </Dropdown>
  );
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["blockquote", { align: [] }],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "color", "background",
  "list", "indent",
  "link", "image",
  "align", "blockquote",
];

const EmailBodyEditor = ({ value, onChange, readOnly, quillRef }) => {
  const insertPlaceholder = (placeholder) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    editor.insertText(range.index, placeholder);
    editor.setSelection(range.index + placeholder.length);
    if (onChange) {
      setTimeout(() => onChange(editor.root.innerHTML), 0);
    }
  };

  return (
    <div className="crm-email-editor">
      <div className="crm-email-editor__placeholder-bar">
        <span style={{ fontSize: 12, color: "#64748B", marginRight: 8 }}>Variables :</span>
        {PLACEHOLDERS.map((p) => (
          <Tag
            key={p.key}
            color="blue"
            style={{ cursor: "pointer", fontSize: 11, marginBottom: 2 }}
            onClick={() => insertPlaceholder(p.key)}
          >
            {p.key}
          </Tag>
        ))}
      </div>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        readOnly={readOnly}
        modules={QUILL_MODULES}
        formats={QUILL_FORMATS}
        placeholder="Rédigez votre email..."
      />
    </div>
  );
};

const StepEditorPanel = ({ step, open, onClose, onUpdate, readOnly }) => {
  const [form] = Form.useForm();
  const quillRef = useRef(null);

  useEffect(() => {
    if (step) {
      form.setFieldsValue({
        ...get(step, "config", {}),
        isAutomatic: step.isAutomatic,
        senderOverride: step.senderOverride,
      });
    }
  }, [step]);

  const debouncedUpdate = useCallback(
    debounce((stepData, formRef) => {
      const values = formRef.getFieldsValue();
      const { isAutomatic, senderOverride, ...config } = values;
      onUpdate({
        config: { ...get(stepData, "config", {}), ...config },
        isAutomatic: isAutomatic !== undefined ? isAutomatic : stepData.isAutomatic,
        senderOverride: senderOverride || null,
      });
    }, 800),
    [onUpdate],
  );

  const handleChange = () => {
    if (readOnly || !step) return;
    debouncedUpdate(step, form);
  };

  const handleBodyChange = (html) => {
    if (readOnly || !step) return;
    form.setFieldValue("bodyTemplate", html);
    debouncedUpdate(step, form);
  };

  if (!step) return null;

  const type = step.type;
  const isEmail = type === "email";

  return (
    <Drawer
      title="Configurer l'étape"
      open={open}
      onClose={onClose}
      width={isEmail ? 640 : 400}
      className="crm-step-editor-drawer"
    >
      <Form form={form} layout="vertical" onValuesChange={handleChange} disabled={readOnly}>
        {isEmail && (
          <>
            <Form.Item name="label" label="Libellé">
              <Input placeholder="Ex: Premier contact" />
            </Form.Item>
            <Form.Item name="subjectTemplate" label="Objet">
              <Input placeholder="Objet de l'email — utilisez {{prénom}} etc." />
            </Form.Item>
            <Form.Item name="bodyTemplate" label="Corps du message" style={{ marginBottom: 8 }}>
              <EmailBodyEditor
                value={form.getFieldValue("bodyTemplate")}
                onChange={handleBodyChange}
                readOnly={readOnly}
                quillRef={quillRef}
              />
            </Form.Item>
            <Form.Item name="isAutomatic" label="Automatique" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="senderOverride" label="Expéditeur spécifique">
              <Input placeholder="Rotation campagne par défaut" />
            </Form.Item>
          </>
        )}

        {(type === "linkedinMessage" || type === "linkedinInvite" || type === "linkedinVisit") && (
          <>
            <Form.Item name="label" label="Libellé">
              <Input placeholder="Ex: Message LinkedIn" />
            </Form.Item>
            <Form.Item name="messageTemplate" label="Message">
              <Input.TextArea rows={3} placeholder="Message template..." />
            </Form.Item>
            <Form.Item name="isAutomatic" label="Automatique" valuePropName="checked">
              <Switch />
            </Form.Item>
          </>
        )}

        {type === "delay" && (
          <>
            <Form.Item name="days" label="Nombre de jours">
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="type" label="Type de jours">
              <Select>
                <Select.Option value="sending_days">Jours ouvrés</Select.Option>
                <Select.Option value="calendar_days">Jours calendaires</Select.Option>
              </Select>
            </Form.Item>
          </>
        )}

        {type === "condition" && (
          <>
            <Form.Item name="conditionType" label="Type de condition">
              <Select placeholder="Sélectionner...">
                {CONDITION_TYPES.map((c) => (
                  <Select.Option key={c.value} value={c.value}>{c.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="timeframeDays" label="Délai (jours)">
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="waitMode" label="Mode d'attente">
              <Select>
                <Select.Option value="within">Dans le délai</Select.Option>
                <Select.Option value="until">Jusqu'à expiration</Select.Option>
              </Select>
            </Form.Item>
          </>
        )}

        {type === "task" && (
          <>
            <Form.Item name="label" label="Libellé">
              <Input placeholder="Ex: Appeler le prospect" />
            </Form.Item>
            <Form.Item name="taskType" label="Type de tâche">
              <Select>
                {TASK_TYPES.map((t) => (
                  <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="instructions" label="Instructions">
              <Input.TextArea rows={3} placeholder="Instructions pour le commercial..." />
            </Form.Item>
            <Form.Item name="blocksProgression" label="Bloque la progression" valuePropName="checked">
              <Switch />
            </Form.Item>
          </>
        )}

        {type === "call" && (
          <>
            <Form.Item name="label" label="Libellé">
              <Input placeholder="Ex: Appel de qualification" />
            </Form.Item>
            <Form.Item name="isAutomatic" label="Automatique" valuePropName="checked">
              <Switch />
            </Form.Item>
          </>
        )}

        {type === "whatsapp" && (
          <>
            <Form.Item name="label" label="Libellé">
              <Input placeholder="Ex: Message WhatsApp" />
            </Form.Item>
            <Form.Item name="messageTemplate" label="Message">
              <Input.TextArea rows={3} placeholder="Message template..." />
            </Form.Item>
          </>
        )}

        {type === "api" && (
          <>
            <Form.Item name="label" label="Libellé">
              <Input placeholder="Ex: Webhook CRM" />
            </Form.Item>
            <Form.Item name="method" label="Méthode">
              <Select>
                <Select.Option value="GET">GET</Select.Option>
                <Select.Option value="POST">POST</Select.Option>
                <Select.Option value="PUT">PUT</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="urlTemplate" label="URL">
              <Input placeholder="https://api.example.com/webhook" />
            </Form.Item>
            <Form.Item name="expectedStatus" label="Code attendu">
              <InputNumber min={100} max={599} style={{ width: "100%" }} placeholder="200" />
            </Form.Item>
            <Form.Item name="failAction" label="En cas d'échec">
              <Select>
                <Select.Option value="skip">Passer</Select.Option>
                <Select.Option value="pause">Mettre en pause</Select.Option>
                <Select.Option value="retry">Réessayer</Select.Option>
              </Select>
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
};

export default StepEditorPanel;
